(() => {
  'use strict';

  const TAG = 'egifts-collection-ux';
  const STATE_PREFIX = 'egifts:collection-ux:v1:';
  const STATE_INDEX_KEY = `${STATE_PREFIX}index`;
  const STATE_TTL = 30 * 60 * 1000;
  const MAX_STATES = 5;

  const number = (value, fallback = 0) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const emitAnalytics = (eventName, payload) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, payload || {}));
  };

  const normalizedParams = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('section_id');
    params.delete('sections');
    return new URLSearchParams([...params.entries()].sort(([a, av], [b, bv]) => a.localeCompare(b) || av.localeCompare(bv)));
  };

  class EgiftsCollectionUX extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.sectionId = this.dataset.sectionId || '';
      this.collectionHandle = this.dataset.collectionHandle || '';
      this.currentSort = this.dataset.currentSort || '';
      this.currentPage = number(this.dataset.currentPage, 1);
      this.totalProducts = number(this.dataset.totalProducts, 0);
      this.visibleProducts = number(this.dataset.initialVisible, 0);
      this.nextUrl = this.dataset.nextUrl || '';
      this.freshAttempted = false;
      this.loading = false;

      this.grid = this.querySelector('#product-grid');
      this.moreGrid = this.querySelector('[data-eg-cux-more-grid]');
      this.fresh = this.querySelector('[data-eg-cux-fresh]');
      this.freshGrid = this.querySelector('[data-eg-cux-fresh-grid]');
      this.controls = this.querySelector('[data-eg-cux-controls]');
      this.moreContent = this.querySelector('[data-eg-cux-more-content]');
      this.loadButton = this.querySelector('[data-eg-cux-load-more]');
      this.buttonLabel = this.querySelector('[data-eg-cux-button-label]');
      this.spinner = this.querySelector('[data-eg-cux-spinner]');
      this.visibleOutput = this.querySelector('[data-eg-cux-visible]');
      this.totalOutput = this.querySelector('[data-eg-cux-total]');
      this.complete = this.querySelector('[data-eg-cux-complete]');
      this.liveRegion = this.querySelector('[data-eg-cux-live]');
      this.errorOutput = this.querySelector('[data-eg-cux-error]');
      this.pagination = this.querySelector('[data-eg-cux-pagination]');

      if (!this.grid || !this.moreGrid || !this.controls) {
        document.documentElement.classList.remove('eg-cux-pending-restore');
        return;
      }

      this.stateKey = this.createStateKey();
      this.onClick = this.handleClick.bind(this);
      this.onPageHide = this.handlePageHide.bind(this);
      this.onPageShow = this.handlePageShow.bind(this);
      this.addEventListener('click', this.onClick);
      window.addEventListener('pagehide', this.onPageHide);
      window.addEventListener('pageshow', this.onPageShow);

      const restored = this.restoreStateIfNeeded();
      this.controls.hidden = false;
      if (this.pagination) this.pagination.hidden = true;
      this.classList.add('is-enhanced');
      this.updateControls();
      this.setHistoryRestoreFlag(false);
      document.documentElement.classList.remove('eg-cux-pending-restore');

      if (restored) {
        requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, restored.scrollY)));
      }
    }

    disconnectedCallback() {
      if (!this._initialized) return;
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('pagehide', this.onPageHide);
      window.removeEventListener('pageshow', this.onPageShow);
      if (this.abortController) this.abortController.abort();
      this._initialized = false;
    }

    createStateKey() {
      return `${STATE_PREFIX}${window.location.pathname}?${normalizedParams().toString()}#${this.sectionId}`;
    }

    historyMarker() {
      const state = history.state;
      return state && state.egiftsCollectionUX ? state.egiftsCollectionUX : null;
    }

    setHistoryRestoreFlag(restore) {
      try {
        const current = history.state && typeof history.state === 'object' ? history.state : {};
        history.replaceState(
          Object.assign({}, current, {
            egiftsCollectionUX: { key: this.stateKey, restore: Boolean(restore), savedAt: Date.now() },
          }),
          '',
          window.location.href
        );
      } catch (error) {}
    }

    handlePageShow(event) {
      if (!event.persisted) return;
      this.setHistoryRestoreFlag(false);
      document.documentElement.classList.remove('eg-cux-pending-restore');
    }

    handlePageHide() {
      const marker = this.historyMarker();
      if (marker && marker.key === this.stateKey && marker.restore) this.saveState();
    }

    handleClick(event) {
      const quickSort = event.target.closest('[data-eg-cux-sort]');
      if (quickSort && this.contains(quickSort)) {
        this.handleQuickSort(event, quickSort);
        return;
      }

      const loadMore = event.target.closest('[data-eg-cux-load-more]');
      if (loadMore && this.contains(loadMore)) {
        event.preventDefault();
        this.loadMore();
        return;
      }

      const productLink = event.target.closest('a[href]');
      if (!productLink || !this.contains(productLink) || event.defaultPrevented) return;
      if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (productLink.target && productLink.target !== '_self') return;

      try {
        const url = new URL(productLink.href, window.location.href);
        if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) return;
        this.saveState();
        this.setHistoryRestoreFlag(true);
      } catch (error) {}
    }

    handleQuickSort(event, link) {
      const selected = link.dataset.egCuxSort;
      if (!selected) return;
      event.preventDefault();
      if (selected === this.currentSort) return;

      const params = new URLSearchParams(window.location.search);
      params.delete('page');
      params.delete('section_id');
      params.delete('sections');
      params.set('sort_by', selected);

      emitAnalytics('collection_quick_sort', {
        collection_handle: this.collectionHandle,
        selected_sort: selected === 'created-descending' ? 'newest' : selected,
      });

      if (typeof FacetFiltersForm !== 'undefined' && typeof FacetFiltersForm.renderPage === 'function') {
        FacetFiltersForm.renderPage(params.toString(), null, true);
      } else {
        window.location.assign(`${window.location.pathname}?${params.toString()}`);
      }
    }

    async loadMore() {
      if (this.loading || !this.nextUrl) return;
      this.setLoading(true);
      this.clearError();
      this.abortController = new AbortController();

      const shouldLoadFresh = !this.freshAttempted && this.currentSort === 'best-selling';
      let freshFirstPagePromise = null;
      if (shouldLoadFresh) {
        freshFirstPagePromise = this.fetchFreshFirstPage(this.abortController.signal)
          .then((root) => ({ root }))
          .catch((error) => ({ error }));
      }

      try {
        const nextRoot = await this.fetchSection(this.nextUrl, this.abortController.signal);

        if (shouldLoadFresh) {
          this.freshAttempted = true;
          try {
            const freshResult = await freshFirstPagePromise;
            if (freshResult.error) throw freshResult.error;
            const incomingHandles = [...nextRoot.querySelectorAll('#product-grid > li[data-eg-cux-product-handle]')]
              .map((item) => item.dataset.egCuxProductHandle)
              .filter(Boolean);
            await this.renderFreshProducts(freshResult.root, this.abortController.signal, incomingHandles);
          } catch (freshError) {
            if (freshError.name !== 'AbortError') console.warn('[E-gifts collection] Nie udało się pobrać sekcji nowości.', freshError);
          }
        }

        const loadedCount = this.appendRankingPage(nextRoot);
        this.updateControls();
        this.saveState();
        this.announce(`Załadowano ${loadedCount} kolejne produkty.`);
        emitAnalytics('collection_load_more', {
          collection_handle: this.collectionHandle,
          loaded_page: this.currentPage,
          products_visible: this.visibleProducts,
          products_total: this.totalProducts,
          current_sort: this.currentSort,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.showError('Nie udało się pobrać produktów. Spróbuj ponownie.');
          this.announce('Nie udało się załadować kolejnych produktów.');
        }
      } finally {
        this.setLoading(false);
      }
    }

    sectionUrl(source) {
      const url = new URL(source, window.location.href);
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', this.sectionId);
      const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (previewThemeId && !url.searchParams.has('preview_theme_id')) {
        url.searchParams.set('preview_theme_id', previewThemeId);
      }
      return url;
    }

    async fetchSection(source, signal) {
      const response = await fetch(this.sectionUrl(source).toString(), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const documentFromServer = new DOMParser().parseFromString(await response.text(), 'text/html');
      const root = documentFromServer.querySelector(TAG);
      if (!root || !root.querySelector('#product-grid')) throw new Error('Brak siatki produktów w odpowiedzi Shopify.');
      return root;
    }

    appendRankingPage(root) {
      const items = [...root.querySelectorAll('#product-grid > li[data-eg-cux-product-handle]')];
      if (!items.length) throw new Error('Shopify nie zwrócił kolejnych produktów.');

      const fragment = document.createDocumentFragment();
      items.forEach((item) => fragment.appendChild(this.prepareCard(item)));
      this.moreGrid.appendChild(fragment);

      this.currentPage = number(root.dataset.currentPage, this.currentPage + 1);
      this.nextUrl = root.dataset.nextUrl || '';
      this.visibleProducts = Math.min(this.totalProducts, this.visibleProducts + items.length);

      this.dispatchEvent(
        new CustomEvent('egifts:collection:products-appended', {
          bubbles: true,
          detail: { page: this.currentPage, count: items.length, container: this.moreGrid },
        })
      );
      return items.length;
    }

    prepareCard(item) {
      item.querySelectorAll('link[rel="stylesheet"]').forEach((link) => link.remove());
      item.classList.remove('scroll-trigger--offscreen');
      item.classList.add('scroll-trigger--cancel');
      item.removeAttribute('style');
      return item;
    }

    freshUrl(page = 1) {
      const params = normalizedParams();
      params.set('sort_by', 'created-descending');
      params.set('page', String(page));
      return `${window.location.pathname}?${params.toString()}`;
    }

    fetchFreshFirstPage(signal) {
      return this.fetchSection(this.freshUrl(1), signal);
    }

    visibleHandles() {
      return new Set(
        [...this.querySelectorAll('[data-eg-cux-product-handle]')]
          .map((item) => item.dataset.egCuxProductHandle)
          .filter(Boolean)
      );
    }

    async renderFreshProducts(firstRoot, signal, additionalExcluded = []) {
      if (!this.fresh || !this.freshGrid || this.currentSort !== 'best-selling') return;

      const excluded = this.visibleHandles();
      additionalExcluded.forEach((handle) => excluded.add(handle));
      const chosen = [];
      let root = firstRoot;
      let fetchedPages = 0;

      while (root && chosen.length < 4 && fetchedPages < 2) {
        fetchedPages += 1;
        const candidates = [...root.querySelectorAll('#product-grid > li[data-eg-cux-product-handle]')];
        for (const candidate of candidates) {
          const handle = candidate.dataset.egCuxProductHandle;
          if (!handle || excluded.has(handle)) continue;
          excluded.add(handle);
          chosen.push(this.prepareCard(candidate));
          if (chosen.length === 4) break;
        }

        if (chosen.length < 4 && root.dataset.nextUrl && fetchedPages < 2) {
          root = await this.fetchSection(root.dataset.nextUrl, signal);
        } else {
          root = null;
        }
      }

      if (!chosen.length) return;
      const fragment = document.createDocumentFragment();
      chosen.forEach((item) => fragment.appendChild(item));
      this.freshGrid.replaceChildren(fragment);
      this.fresh.hidden = false;
    }

    setLoading(isLoading) {
      this.loading = isLoading;
      if (!this.loadButton) return;
      this.loadButton.disabled = isLoading;
      this.loadButton.setAttribute('aria-busy', String(isLoading));
      if (this.spinner) this.spinner.hidden = !isLoading;
      if (this.buttonLabel) this.buttonLabel.textContent = isLoading ? 'ŁADOWANIE PRODUKTÓW' : 'POKAŻ WIĘCEJ PRODUKTÓW';
    }

    updateControls() {
      if (this.visibleOutput) this.visibleOutput.textContent = String(this.visibleProducts);
      if (this.totalOutput) this.totalOutput.textContent = String(this.totalProducts);
      const hasMore = Boolean(this.nextUrl) && this.visibleProducts < this.totalProducts;
      if (this.moreContent) this.moreContent.hidden = !hasMore;
      if (this.complete) this.complete.hidden = hasMore;
    }

    announce(message) {
      if (!this.liveRegion) return;
      this.liveRegion.textContent = '';
      requestAnimationFrame(() => { this.liveRegion.textContent = message; });
    }

    clearError() {
      if (!this.errorOutput) return;
      this.errorOutput.hidden = true;
      this.errorOutput.textContent = '';
    }

    showError(message) {
      if (!this.errorOutput) return;
      this.errorOutput.textContent = message;
      this.errorOutput.hidden = false;
    }

    saveState() {
      try {
        const state = {
          version: 1,
          savedAt: Date.now(),
          collectionHandle: this.collectionHandle,
          currentSort: this.currentSort,
          currentPage: this.currentPage,
          totalProducts: this.totalProducts,
          visibleProducts: this.visibleProducts,
          nextUrl: this.nextUrl,
          freshAttempted: this.freshAttempted,
          freshVisible: Boolean(this.fresh && !this.fresh.hidden),
          freshHtml: this.freshGrid ? this.freshGrid.innerHTML : '',
          moreHtml: this.moreGrid ? this.moreGrid.innerHTML : '',
          scrollY: Math.max(0, Math.round(window.scrollY)),
        };
        sessionStorage.setItem(this.stateKey, JSON.stringify(state));
        this.rememberStateKey();
      } catch (error) {}
    }

    rememberStateKey() {
      let keys = [];
      try { keys = JSON.parse(sessionStorage.getItem(STATE_INDEX_KEY) || '[]'); } catch (error) {}
      keys = [this.stateKey, ...keys.filter((key) => key !== this.stateKey)];
      keys.slice(MAX_STATES).forEach((key) => sessionStorage.removeItem(key));
      sessionStorage.setItem(STATE_INDEX_KEY, JSON.stringify(keys.slice(0, MAX_STATES)));
    }

    restoreStateIfNeeded() {
      const marker = this.historyMarker();
      if (!marker || !marker.restore || marker.key !== this.stateKey) return null;

      try {
        const state = JSON.parse(sessionStorage.getItem(this.stateKey) || 'null');
        if (
          !state ||
          state.version !== 1 ||
          Date.now() - state.savedAt > STATE_TTL ||
          state.collectionHandle !== this.collectionHandle ||
          state.currentSort !== this.currentSort ||
          state.totalProducts !== this.totalProducts
        ) return null;

        this.moreGrid.innerHTML = state.moreHtml || '';
        if (this.freshGrid) this.freshGrid.innerHTML = state.freshHtml || '';
        if (this.fresh) this.fresh.hidden = !state.freshVisible;
        this.currentPage = number(state.currentPage, this.currentPage);
        this.visibleProducts = number(state.visibleProducts, this.visibleProducts);
        this.nextUrl = state.nextUrl || '';
        this.freshAttempted = Boolean(state.freshAttempted);
        this.querySelectorAll('[data-eg-cux-product-handle]').forEach((item) => this.prepareCard(item));
        return { scrollY: number(state.scrollY, 0) };
      } catch (error) {
        return null;
      }
    }
  }

  if (!customElements.get(TAG)) customElements.define(TAG, EgiftsCollectionUX);
})();